/**
 * ============================================================================
 * Utility Functions
 * ============================================================================
 *
 * Shared utility functions used across the application.
 * Feature-specific utilities should be defined in their respective feature folders.
 * ============================================================================
 */

/* ----------------------------------------------------------------------------
   Class Name Utilities
   ---------------------------------------------------------------------------- */

/**
 * Combines class names, filtering out falsy values
 * @example cn('base', isActive && 'active', className)
 */
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(" ");
}

/* ----------------------------------------------------------------------------
   String Utilities
   ---------------------------------------------------------------------------- */

/**
 * Capitalizes the first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Converts a string to kebab-case
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

/**
 * Truncates text to a specified length with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

/* ----------------------------------------------------------------------------
   Validation Utilities
   ---------------------------------------------------------------------------- */

/**
 * Validates an email address
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates a UAE phone number
 */
export function isValidUAEPhone(phone: string): boolean {
  const uaePhoneRegex = /^(?:\+971|00971|971|0)?(?:50|52|54|55|56|58|2|3|4|6|7|9)\d{7}$/;
  return uaePhoneRegex.test(phone.replace(/[\s-]/g, ""));
}

/* ----------------------------------------------------------------------------
   Formatting Utilities
   ---------------------------------------------------------------------------- */

/**
 * Formats a number as currency (AED)
 */
export function formatCurrency(amount: number, currency = "AED"): string {
  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a date string
 */
export function formatDate(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-AE", options).format(dateObj);
}

/* ----------------------------------------------------------------------------
   DOM Utilities
   ---------------------------------------------------------------------------- */

/**
 * Scrolls to an element smoothly
 */
export function scrollToElement(elementId: string, offset = 0): void {
  const element = document.getElementById(elementId);
  if (element) {
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }
}

/**
 * Copies text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* ----------------------------------------------------------------------------
   Type Guards
   ---------------------------------------------------------------------------- */

/**
 * Checks if a value is defined (not null or undefined)
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Checks if a value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
